import React, {useState, useCallback, useEffect, useRef} from 'react';
import {useDispatch, useSelector} from 'react-redux';
import {useTranslation} from 'react-i18next';
import {
    createProductSnapshotRequest,
    deleteProductSnapshotRequest,
    updateProductSnapshotRequest
} from "../store/order/actions/order-actions";
import AddProductSnapshot from "./AddProductSnapshot";
import ProductCodeAutocomplete from "./ProductCodeAutocomplete";
const ProductSnapshotsSection = ({ order, existingSnapshots = [], error }) => {
    const { t } = useTranslation();
    const isOrderDone = order?.status === 'DONE' || order?.status === 'CLOSED';


    const dispatch = useDispatch();
    const productSnapshotCreationStatus = useSelector(state => state.order?.productSnapshotCreationStatus);
    const prevStatusRef = useRef();
    const [showNewProductRow, setShowNewProductRow] = useState(false);
    const [editingIndex, setEditingIndex] = useState(null);
    const [updateProductSnapshotData, setUpdateProductSnapshotData] = useState({});
    const [newSnapshot, setNewSnapshot] = useState({
        name: '',
        price: 0.00,
        currency: 'EUR',
        quantity: 0,
        discount: 0.00,
        code: '',
        description: '',
        type: 'UNKNOWN',
        company: '',
        productId: null,
    });

    const [editableSnapshots, setEditableSnapshots] = useState(
        [...existingSnapshots].sort((a, b) => a.id.snapshotId - b.id.snapshotId)
    );

    useEffect(() => {
        setEditableSnapshots([...existingSnapshots].sort((a, b) => a.id.snapshotId - b.id.snapshotId));
    }, [existingSnapshots]);

    // Shared function to reset form and close it
    const resetAndCloseForm = useCallback(() => {
        setShowNewProductRow(false);
        setNewSnapshot({
            name: '', price: 0.00, currency: 'EUR', quantity: 0, discount: 0.00,
            code: '', description: '', type: 'UNKNOWN', company: '', productId: null,
        });
    }, []);

    // Track snapshot count to detect successful adds
    useEffect(() => {
        const currentCount = existingSnapshots.length;
        const prevCount = prevStatusRef.current?.count;

        // If count increased and form is open, it means a successful add happened
        if (prevCount !== undefined && currentCount > prevCount && showNewProductRow) {
            console.log('Successful add detected - closing form');
            resetAndCloseForm();
        }
        
        // Update ref with current count
        prevStatusRef.current = { count: currentCount };
    }, [existingSnapshots.length, showNewProductRow, resetAndCloseForm]);

    const handleAddProduct = useCallback((e) => {
        e.preventDefault()
        if (!order?.id) return;
        
        const payload = { ...newSnapshot };
        if (!payload.productId) {
            delete payload.productId;
        }
        
        dispatch(createProductSnapshotRequest(order.id, payload));
    }, [newSnapshot, dispatch, order?.id]);

    const handleNewSnapshotChange = (field, value) => {
        setNewSnapshot((prev) => ({ ...prev, [field]: field === 'price' || field === 'quantity' || field === 'discount' ? Number(value) : value }));
    };

    const handleProductSelectForNew = (product) => {
        setNewSnapshot((prev) => ({
            ...prev,
            code: product.code,
            name: product.name,
            price: product.price || 0,
            description: product.description || '',
            type: product.type || 'UNKNOWN',
            company: product.company || '',
            productId: product.id,
        }));
    };

    const handleCodeChangeForNew = (code) => {
        setNewSnapshot((prev) => ({
            ...prev,
            code: code,
            productId: null,
        }));
    };

    const handleEditSnapshotChange = (index, field, value) => {
        setEditableSnapshots((prev) =>
            prev.map((snap, i) =>
                i === index ? { ...snap, [field]: field === 'price' || field === 'quantity' || field === 'discount' ? Number(value) : value } : snap
            )
        );

        setUpdateProductSnapshotData((prev) => ({
            id: editableSnapshots[index]?.id,
            ...prev,
            [field]: { value: field === 'price' || field === 'quantity' || field === 'discount' ? Number(value) : value }
        }));
    };

    const handleProductSelectForEdit = (index, product) => {
        setEditableSnapshots((prev) =>
            prev.map((snap, i) =>
                i === index ? {
                    ...snap,
                    code: product.code,
                    name: product.name,
                    price: product.price || 0,
                    description: product.description || '',
                    type: product.type || 'UNKNOWN',
                    company: product.company || '',
                } : snap
            )
        );

        setUpdateProductSnapshotData((prev) => ({
            id: editableSnapshots[index]?.id,
            ...prev,
            code: { value: product.code },
            name: { value: product.name },
            price: { value: product.price || 0 },
            description: { value: product.description || '' },
            type: { value: product.type || 'UNKNOWN' },
            company: { value: product.company || '' },
            productId: { value: product.id },
        }));
    };

    const handleCodeChangeForEdit = (index, code) => {
        handleEditSnapshotChange(index, 'code', code);
    };

    const handleQuantityChange = (index, value) => {
        setEditableSnapshots((prevSnapshots) => {
            // Create a new array (spread previous state)
            const updatedSnapshots = [...prevSnapshots];

            // Create a new object for the updated snapshot
            updatedSnapshots[index] = {
                ...updatedSnapshots[index],
                quantity: Number(value)
            };

            return updatedSnapshots;
        });

        // Dispatch update action with correct Redux structure
        dispatch(updateProductSnapshotRequest({
            id: editableSnapshots[index].id,
            quantity: { value: Number(value) }
        }));
    };

    const handleAddProductClick = () => {
        setShowNewProductRow(true);
    };

    const handleCancelProductClick = () => {
        resetAndCloseForm();
    };

    const handleEditClick = (index) => {
        setEditingIndex(index);
    };

    const handleSaveEdit = () => {
        if (!order?.id || !updateProductSnapshotData.id) {
            setEditingIndex(null);
            setUpdateProductSnapshotData({});
            return;
        }
        dispatch(updateProductSnapshotRequest(updateProductSnapshotData));
        if(error) {
            return;
        }
        setEditingIndex(null);
        setUpdateProductSnapshotData({});
    };

    const handleDelete = (productSnapshotId) => {
        if (!order?.id || !productSnapshotId) return;
        dispatch(deleteProductSnapshotRequest({
            orderId: order.id,
            snapshotId: productSnapshotId,
        }));
    };

    return (
        <div className="bg-white p-6 m-top-2 shadow-lg rounded-lg">
            <h2 className="text-2xl font-semibold text-[#34495e] mb-4">{t('order.productSnapshot')}</h2>
            {editableSnapshots.length > 0 && (
                <table className="border-collapse table-auto w-full text-sm">
                    <thead>
                    <tr className="bg-gray-100">
                        <th className="border-b p-4 pl-8 text-gray-600">{t('common.name')}</th>
                        <th className="border-b p-4 pl-8 text-gray-600">{t('product.price')}</th>
                        <th className="border-b p-4 pl-8 text-gray-600">{t('product.currency')}</th>
                        <th className="border-b p-4 pl-8 text-gray-600">{t('calculation.quantity')}</th>
                        <th className="border-b p-4 pl-8 text-gray-600">{t('order.discount')}</th>
                        <th className="border-b p-4 pl-8 text-gray-600">{t('product.code')}</th>
                        <th className="border-b p-4 pl-8 text-gray-600">{t('product.description')}</th>
                        <th className="border-b p-4 pl-8 text-gray-600">{t('product.type')}</th>
                        <th className="border-b p-4 pl-8 text-gray-600">{t('product.company')}</th>
                        {!isOrderDone && <th className="border-b p-4 pl-8 text-gray-600">{t('common.actions')}</th>}
                    </tr>
                    </thead>
                    <tbody className="bg-white text-center">
                    {editableSnapshots.map((snap, index) => (
                        <tr key={index}>
                            {Object.keys(newSnapshot).filter(f => f !== 'productId').map((field) => (
                                <td key={field} className="border-b p-4 pl-8 text-gray-700">
                                    {isOrderDone ? (
                                        // Read-only view for DONE orders
                                        snap[field]
                                    ) : field === 'quantity' || index === editingIndex ? (
                                        field === 'currency' || field === 'type' ? (
                                            // Select inputs for Currency & Type
                                            <select
                                                value={snap[field]}
                                                onChange={(e) => handleEditSnapshotChange(index, field, e.target.value)}
                                                className="border p-1 rounded w-full"
                                            >
                                                {field === 'currency' ? (
                                                    <>
                                                        <option value="EUR">EUR</option>
                                                        {/*<option value="RSD">RSD</option>*/}
                                                    </>
                                                ) : (
                                                    <>
                                                        <option value="VIDEO">VIDEO</option>
                                                        <option value="AUDIO">AUDIO</option>
                                                        <option value="LIGHTING">LIGHTING</option>
                                                        <option value="UNKNOWN">UNKNOWN</option>
                                                    </>
                                                )}
                                            </select>
                                        ) : field === 'code' && index === editingIndex ? (
                                            // Product code autocomplete for edit mode
                                            <ProductCodeAutocomplete
                                                value={snap[field]}
                                                onProductSelect={(product) => handleProductSelectForEdit(index, product)}
                                                onCodeChange={(code) => handleCodeChangeForEdit(index, code)}
                                                className="border p-1 rounded w-full"
                                            />
                                        ) : (
                                            // Numeric & text inputs
                                            <input
                                                type={field === 'price' || field === 'discount' || field === 'quantity' ? 'number' : 'text'}
                                                step={field === 'price' || field === 'discount' ? '0.01' : '1'}
                                                value={snap[field]}
                                                onChange={(e) => {
                                                    let value = e.target.value;

                                                    // Convert to proper numeric type if needed
                                                    if (field === 'price' || field === 'discount') {
                                                        value = parseFloat(value);
                                                    } else if (field === 'quantity') {
                                                        value = parseInt(value, 10);
                                                    }

                                                    // Handle quantity separately when not in edit mode
                                                    if (field === 'quantity' && index !== editingIndex) {
                                                        handleQuantityChange(index, value);
                                                    } else {
                                                        handleEditSnapshotChange(index, field, value);
                                                    }
                                                }}
                                                className="border p-1 rounded w-full"
                                            />
                                        )
                                    ) : (
                                        snap[field]
                                    )}
                                </td>
                            ))}
                            {!isOrderDone && (
                                <td className="border-b p-4 pl-8 text-gray-700">
                                    {index === editingIndex ? (
                                        <div className="flex gap-2">
                                            <button onClick={handleSaveEdit} className="text-green-500 text-lg">✅</button>
                                            <button onClick={() => handleDelete(snap.id.snapshotId)}
                                                    className="text-red-500 text-lg">🗑️
                                            </button>
                                        </div>
                                    ) : (
                                        <button onClick={() => handleEditClick(index)}
                                                className="text-blue-500 text-lg">✏️</button>
                                    )}
                                </td>
                            )}
                        </tr>
                    ))}
                    </tbody>
                </table>
            )}
            {!isOrderDone && (
                <AddProductSnapshot
                    newSnapshot={newSnapshot}
                    showNewProductRow={showNewProductRow}
                    handleAddProduct={handleAddProduct}
                    handleCancelProductClick={handleCancelProductClick}
                    handleAddProductClick={handleAddProductClick}
                    handleNewSnapshotChange={handleNewSnapshotChange}
                    handleProductSelect={handleProductSelectForNew}
                    handleCodeChange={handleCodeChangeForNew}
                />
            )}
        </div>
);
};

export default ProductSnapshotsSection;
